import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import RecordTable from "components/RecordTable";

import { adminApi } from "services/api";
import { courseLabel, COURSE_OPTIONS } from "utils/courses";
import CallButton from "components/CallButton";
import { webinarRegistrationsToRows } from "utils/exportUtils";

const STATUS_COLORS = {
  registered: "info",
  contacted: "warning",
  enrolled: "success",
  rejected: "error",
  pending: "default",
  attended: "success",
  missed: "warning",
};

const STATUS_OPTIONS = [
  "registered",
  "contacted",
  "enrolled",
  "rejected",
  "pending",
  "attended",
  "missed",
];
const STAGE_FILTER_OPTIONS = STATUS_OPTIONS.map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1),
}));
const EXPORT_CONFIG = {
  fetchAll: adminApi.exportWebinarRegistrations,
  toRows: webinarRegistrationsToRows,
  filename: "webinar-registrations",
  title: "Webinar Registrations",
};

// Below md the table becomes stacked cards: give touch targets ~40px there, keeping desktop sizes.
const ROW_ACTION_SX = { width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } };
const SMALL_BUTTON_TAP_SX = { minHeight: { xs: "2.5rem", md: "2rem" } };

function WebinarRegistrations() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [reloadToken, setReloadToken] = useState(0);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Message dialog
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null);
  const [channels, setChannels] = useState(["email"]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [msgSuccess, setMsgSuccess] = useState("");

  // Status update
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [statusError, setStatusError] = useState("");

  const handleViewDetails = async (reg) => {
    setSelected(reg);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await adminApi.getWebinarRegistration(reg._id);
      setSelected(data.registration);
    } catch {
      // keep initial data
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenMessage = (reg, e) => {
    e.stopPropagation();
    setMsgTarget(reg);
    setChannels(["email"]);
    setSubject("");
    setMessage("");
    setMsgError("");
    setMsgSuccess("");
    setMsgOpen(true);
  };

  const handleChannelToggle = (_, next) => {
    if (next.length > 0) setChannels(next);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      setMsgError("Message is required");
      return;
    }
    if (channels.includes("email") && !subject.trim()) {
      setMsgError("Subject is required when sending via email");
      return;
    }
    try {
      setSending(true);
      setMsgError("");
      const result = await adminApi.sendMessageToWebinarRegistrant(msgTarget._id, {
        subject,
        message,
        channels,
      });
      const labels = { email: "email", whatsapp: "WhatsApp", sms: "SMS" };
      if (result.results) {
        const failed = Object.entries(result.results)
          .filter(([, r]) => !r.success)
          .map(([ch]) => labels[ch] || ch);
        const attempted = Object.keys(result.results).length;
        if (failed.length && failed.length >= attempted) {
          setMsgError(`Nothing was sent: ${failed.join(", ")} failed.`);
        } else if (failed.length) {
          setMsgError(`Delivered, but failed on: ${failed.join(", ")}`);
        } else {
          setMsgSuccess(
            `Sent via ${channels.map((c) => labels[c]).join(", ")} to ${msgTarget.email}`
          );
        }
      } else {
        setMsgSuccess(`Message sent to ${msgTarget.email}`);
      }
      setSubject("");
      setMessage("");
    } catch (err) {
      setMsgError(err.response?.data?.error || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (reg, newStatus) => {
    try {
      setStatusUpdating(reg._id);
      setStatusError("");
      await adminApi.updateWebinarRegistrationStatus(reg._id, newStatus);
      setReloadToken((t) => t + 1);
      if (selected && selected._id === reg._id) {
        setSelected((s) => ({ ...s, stage: newStatus }));
      }
    } catch (err) {
      // The dropdown is driven by the row's saved stage, so it snaps back on its own.
      setStatusError(
        err.response?.data?.error || "Could not change the status. It was left as it was."
      );
    } finally {
      setStatusUpdating(null);
    }
  };

  const columns = [
    { Header: "name", accessor: "name", width: "14%", align: "left" },
    { Header: "email", accessor: "email", width: "16%", align: "left" },
    { Header: "phone", accessor: "phone", width: "13%", align: "left" },
    { Header: "location", accessor: "location", width: "11%", align: "left" },
    { Header: "course", accessor: "course", width: "14%", align: "left" },
    { Header: "status", accessor: "status", width: "12%", align: "center" },
    { Header: "date", accessor: "date", width: "9%", align: "left" },
    { Header: "actions", accessor: "actions", width: "8%", align: "center" },
  ];

  const renderRow = (reg) => ({
    name: (
      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        onClick={() => handleViewDetails(reg)}
      >
        {reg.name}
      </MDTypography>
    ),
    email: <MDTypography variant="caption">{reg.email}</MDTypography>,
    phone: (
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDTypography variant="caption">{reg.phone || "—"}</MDTypography>
        <CallButton phoneNumber={reg.phone} />
      </MDBox>
    ),
    location: <MDTypography variant="caption">{reg.location || "—"}</MDTypography>,
    course: reg.courseSlug ? (
      <Chip label={courseLabel(reg.courseSlug)} size="small" color="secondary" variant="outlined" />
    ) : (
      <MDTypography variant="caption">—</MDTypography>
    ),
    status: (
      <TextField
        select
        size="small"
        value={reg.stage || "registered"}
        disabled={statusUpdating === reg._id}
        onChange={(e) => handleStatusChange(reg, e.target.value)}
        sx={{ minWidth: 120, width: { xs: "100%", md: "auto" } }}
        onClick={(e) => e.stopPropagation()}
      >
        {STATUS_OPTIONS.map((s) => (
          <MenuItem key={s} value={s}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </MenuItem>
        ))}
      </TextField>
    ),
    date: (
      <MDTypography variant="caption">
        {reg.submittedAt ? new Date(reg.submittedAt).toLocaleDateString() : "—"}
      </MDTypography>
    ),
    actions: (
      <MDBox display="flex" gap={0.5} justifyContent="center">
        <Tooltip title="View details">
          <IconButton size="small" sx={ROW_ACTION_SX} onClick={() => handleViewDetails(reg)}>
            <Icon fontSize="small">visibility</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Send message">
          <IconButton
            size="small"
            color="primary"
            sx={ROW_ACTION_SX}
            onClick={(e) => handleOpenMessage(reg, e)}
          >
            <Icon fontSize="small">send</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Webinar Registrations
                  </MDTypography>
                  <MDTypography variant="caption" color="white">
                    Every registration from the webinar pages
                  </MDTypography>
                </MDBox>
              </MDBox>

              {statusError && (
                <MDBox px={2} pt={2}>
                  <MDAlert color="error" dismissible onClose={() => setStatusError("")}>
                    {statusError}
                  </MDAlert>
                </MDBox>
              )}

              <RecordTable
                columns={columns}
                fetchPage={adminApi.getWebinarRegistrations}
                renderRow={renderRow}
                statusOptions={STAGE_FILTER_OPTIONS}
                statusLabel="Stage"
                courseOptions={COURSE_OPTIONS}
                reloadToken={reloadToken}
                exportConfig={EXPORT_CONFIG}
                emptyMessage="No registrations yet"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ── Detail Dialog ── */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
      >
        <DialogTitle>
          Registrant Details
          {selected && (
            <Chip
              label={selected.stage || "registered"}
              color={STATUS_COLORS[selected.stage] || "default"}
              size="small"
              sx={{ ml: 2, verticalAlign: "middle" }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {detailLoading && (
            <MDTypography variant="body2" mb={1}>
              Loading...
            </MDTypography>
          )}
          {selected && (
            <>
              {[
                { label: "Name", value: selected.name },
                { label: "Email", value: selected.email },
                { label: "WhatsApp", value: selected.phone },
                { label: "Location", value: selected.location },
                { label: "Course", value: courseLabel(selected.courseSlug) },
                {
                  label: "Submitted",
                  value: selected.submittedAt
                    ? new Date(selected.submittedAt).toLocaleString()
                    : null,
                },
              ]
                .filter((f) => f.value)
                .map((f) => (
                  <MDBox key={f.label} mb={1.5}>
                    <MDTypography variant="button" fontWeight="bold">
                      {f.label}:
                    </MDTypography>{" "}
                    <MDTypography variant="body2" component="span">
                      {f.value}
                    </MDTypography>
                  </MDBox>
                ))}

              {selected.journey && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                    Q1 — Where are you in your tech journey?
                  </MDTypography>
                  <MDTypography variant="body2">{selected.journey}</MDTypography>
                </>
              )}

              {selected.frustration && (
                <MDBox mt={1.5}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                    Q2 — Biggest frustration right now?
                  </MDTypography>
                  <MDTypography variant="body2">{selected.frustration}</MDTypography>
                </MDBox>
              )}

              {selected.readiness && (
                <MDBox mt={1.5}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                    Q3 — How ready to take action?
                  </MDTypography>
                  <MDTypography variant="body2">{selected.readiness}</MDTypography>
                </MDBox>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selected && (
            <MDButton
              color="primary"
              size="small"
              startIcon={<Icon>send</Icon>}
              sx={SMALL_BUTTON_TAP_SX}
              onClick={(e) => {
                setDetailOpen(false);
                handleOpenMessage(selected, e);
              }}
            >
              Send Message
            </MDButton>
          )}
          <MDButton onClick={() => setDetailOpen(false)} color="secondary">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Send Message Dialog ── */}
      <Dialog open={msgOpen} onClose={() => setMsgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Message to {msgTarget?.name}
          <MDTypography variant="caption" display="block" color="secondary">
            {msgTarget?.email} · {msgTarget?.phone || "no phone"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          {msgError && (
            <MDBox mb={2}>
              <MDAlert color="error" dismissible onClose={() => setMsgError("")}>
                {msgError}
              </MDAlert>
            </MDBox>
          )}
          {msgSuccess && (
            <MDBox mb={2}>
              <MDAlert color="success" dismissible onClose={() => setMsgSuccess("")}>
                {msgSuccess}
              </MDAlert>
            </MDBox>
          )}
          <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
            Send via
          </MDTypography>
          <ToggleButtonGroup
            value={channels}
            onChange={handleChannelToggle}
            size="small"
            fullWidth={isPhone}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="email">
              <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>email</Icon>Email
            </ToggleButton>
            <ToggleButton value="whatsapp">
              <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>chat</Icon>WhatsApp
            </ToggleButton>
            <ToggleButton value="sms">
              <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>sms</Icon>SMS
            </ToggleButton>
          </ToggleButtonGroup>
          {channels.includes("email") && (
            <TextField
              label="Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />
          )}
          <TextField
            label="Message"
            fullWidth
            multiline
            minRows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message to the registrant..."
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setMsgOpen(false)} color="secondary" disabled={sending}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSendMessage}
            color="primary"
            disabled={sending || !!msgSuccess}
            startIcon={<Icon>send</Icon>}
          >
            {sending ? "Sending..." : `Send via ${channels.join(" + ")}`}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default WebinarRegistrations;
